import type { DropdownOption } from 'components/DropdownListWithOptions';
import { DISALLOWED_CHARACTERS } from 'constants/invoices';
import type { FactoryApp } from 'factories/apps';
import { sum } from 'lodash-es';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import React from 'react';
import type { InvoiceQuery } from 'redux/modules/invoice.types';
import { InvoiceListFields } from 'redux/modules/invoice.types';
import { extractProjectBase } from 'redux/selectors/utils/projects';
import type { Account } from 'types/account';
import type { AppAccount } from 'types/apps';
import type {
  ExternalReference,
  InvoicePreviewCreationResponse,
  InvoicePreviewProjectDetails,
  InvoiceSkippedItemDetails,
  RawInvoice,
} from 'types/invoice';
import * as types from 'types/invoice';
import { InvoiceStatus } from 'types/invoice';
import type { ProjectBase, ProjectVariant } from 'types/project';
import { checkStringForPattern, removeCharactersFromString } from 'utils/string';
import { orderBy } from 'utils/typesafe';

export function invoiceRulesFactory(account: Account): types.RawInvoiceRules {
  const { id, name, color, currency, logo } = account;
  const timestamp = new Date().toISOString();
  return {
    id: 0,
    created_at: timestamp,
    updated_at: timestamp,
    account: {
      id,
      name,
      color,
      currency,
      logo,
    },
    invoice_method: types.InvoiceMethods.Automatic,
    standard_invoice_date: types.InvoiceDates.FirstOfMonth,
    invoice_by: types.InvoiceBy.Project,
    list_item_grouping: types.ItemGrouping.Users,
  };
}

export function invoiceDraftFactory(
  seed: Partial<types.InvoiceDraft>,
  rules?: types.RawInvoiceRules | null
): types.InvoiceDraft {
  const now = DateTime.now().toISO();
  return {
    id: 0,
    name: `Invoice Name ${now}`,
    description: 'description',
    date: now,
    from: null,
    to: null,
    due_date: null,
    created_at: now,
    updated_at: now,
    paid: false,
    external_references: [],
    external_total_cost: { fractional: 0, amount: 0, formatted: '0.00', currency_code: '' },
    total_cost: { fractional: 0, amount: 0, formatted: '0.00', currency_code: '' },
    total_hours: 0,
    lines: [],
    user: {
      id: 0,
      email: '',
      name: '',
      updated_at: 0,
      avatar: {
        medium_retina: '',
        large_retina: '',
      },
    },
    invoice_by: rules?.invoice_by || types.InvoiceBy.Project,
    list_item_grouping: rules?.list_item_grouping || types.ItemGrouping.Users,
    filters: {},
    status: InvoiceStatus.LocalDraft,
    project: null,
    deleted: false,
    ...seed,
  };
}

export function invoiceDraftToRequestFactory(
  draft: Partial<types.InvoiceDraft>
): types.InvoiceRequestBody {
  const {
    name = '',
    description = 'description',
    date = '',
    from = '',
    to = '',
    invoice_by = types.InvoiceBy.Project,
    list_item_grouping = types.ItemGrouping.Users,
    filters = { project_ids: [] },
  } = draft;

  return {
    name,
    description,
    date,
    from,
    to,
    invoice_by,
    list_item_grouping,
    filters,
    connection_id: draft.connectionId,
  };
}

export function sortInvoicesFactory(
  invoices: Array<types.RawInvoice>,
  sortBy: {
    field: InvoiceListFields;
    direction?: 'asc' | 'desc';
  }
) {
  // lodash does a case sensitive sort, so lower case user generated fields
  if (sortBy.field === InvoiceListFields.User) {
    return orderBy(
      invoices,
      [invoice => invoice?.user?.name?.toLowerCase()],
      [sortBy.direction || 'asc']
    );
  }
  if (sortBy.field === InvoiceListFields.ClientName) {
    return orderBy(
      invoices,
      [invoice => invoice?.project?.client?.name?.toLowerCase()],
      [sortBy.direction || 'asc']
    );
  }

  if (sortBy.field === InvoiceListFields.Integration) {
    return orderBy(
      invoices,
      [invoice => invoice.external_references?.length],
      [sortBy.direction || 'asc']
    );
  }

  if (sortBy.field === InvoiceListFields.TotalCost) {
    return orderBy(invoices, [invoice => invoice.total_cost.amount], [sortBy.direction || 'asc']);
  }

  return orderBy(invoices, [sortBy.field], [sortBy.direction || 'desc']);
}

export interface InvoiceEditorConnectionOption extends AppAccount {
  name: string;
  icon: JSX.Element;
  provider: string;
}

export type InvoiceEditorOption = InvoiceEditorConnectionOption | HeadingOption;

export function invoiceEditorConnectionsFactory(
  appAccounts: Record<number, Array<AppAccount>>,
  appEntities: Record<number, FactoryApp>
) {
  const appAccountIds = Object.keys(appAccounts).map(Number);
  if (!appAccountIds.length) {
    return [];
  }

  return appAccountIds.reduce((prev: Array<InvoiceEditorOption>, appAccountId: number, idx) => {
    const account: Array<AppAccount> = appAccounts[appAccountId];
    const app = appEntities[appAccountId];
    const icon = <img key={appAccountId} alt={app?.provider} src={app?.logoUrl} />;

    const appExportOption: Array<InvoiceEditorOption> = account.reduce(
      (previous: Array<InvoiceEditorOption>, a) => {
        const currentEntry: InvoiceEditorConnectionOption = {
          ...a,
          name: a.email,
          provider: app.displayName,
          active: false,
          icon,
        };

        const header: HeadingOption = {
          type: 'header',
          name: app.displayName,
        };

        if (!previous.length) {
          return [...previous, ...[header, currentEntry]];
        }
        const previousEntry = previous[idx - 1];

        // @ts-expect-error This looks like a bug to me?
        if (previousEntry?.name !== app?.name) {
          return [...previous, header, currentEntry];
        }
        return [...previous, currentEntry];
      },
      []
    );

    return [...prev, ...appExportOption];
  }, []);
}

interface AppAccountExportOptions extends AppAccount {
  isSynced: boolean;
  name: string;
  isExported?: boolean;
  disabled?: boolean;
}

export function exportOptionsFactory(
  appAccounts: Record<number, Array<AppAccount>>,
  appEntities: Record<number, FactoryApp>,
  clientExternalReferences: Array<ExternalReference>,
  invoiceExternalReferences: Array<ExternalReference>
) {
  const invoiceConnectionIds = invoiceExternalReferences.length
    ? invoiceExternalReferences.map(ref => ref.connection_id)
    : null;

  const appAccountIds = Object.keys(appAccounts).map(Number);
  const connectionIds = clientExternalReferences.length
    ? clientExternalReferences.map(ref => ref.connection_id)
    : null;

  if (!appAccountIds.length) {
    return [];
  }

  return appAccountIds.reduce((prev: Array<AppAccountExportOptions>, id: number) => {
    const account: Array<AppAccount> = appAccounts[id];
    const app = appEntities[id];

    // todo revisit this & tidy, invoices should not be able to have more than one connection soon
    if (invoiceConnectionIds && app) {
      const id = invoiceConnectionIds[0];
      const accountForConnection = account.find(a => a.id === id);

      if (!accountForConnection) {
        return prev;
      }

      const appExportOption: Array<AppAccountExportOptions> = [
        {
          ...accountForConnection,
          isSynced: true,
          name: app?.displayName,
        },
      ];

      return appExportOption;
    }

    const appExportOption: Array<AppAccountExportOptions> = account.map(a => {
      return {
        ...a,
        disabled: false,
        isSynced: false,
        isExported: connectionIds?.includes(a.id),
        name: app?.displayName,
      };
    });

    return [...prev, ...appExportOption];
  }, []);
}

export interface InvoiceExportItem extends AppAccountExportOptions {
  provider: string;
  active: boolean;
  confirm: boolean;
  confirmMessage: ReactNode;
  icon: JSX.Element;
}

export interface InvoiceHeadingOption {
  type: string;
  name: string;
}

export type HeadingOption = InvoiceHeadingOption & DropdownOption;
export type ListOption = InvoiceExportItem & DropdownOption;

export type InvoiceExportOption = ListOption | HeadingOption;

export function getInvoiceOptions(
  invoice: RawInvoice | null,
  appEntities: Record<number, FactoryApp>,
  appAccounts: Record<number, Array<AppAccount>>
) {
  const clientExternalReferences = invoice?.external_references || [];
  const exportOptions = exportOptionsFactory(
    appAccounts,
    appEntities,
    clientExternalReferences,
    invoice?.external_references || []
  );
  const apps: Array<FactoryApp> = Object.keys(appEntities).map(id => appEntities[Number(id)]);

  const invoiceListOptions = exportOptions.reduce(
    (prev: Array<InvoiceExportOption>, curr: AppAccountExportOptions, idx) => {
      const clientName = invoice?.project?.client.name ?? '';
      const maybeHasInvalidClientName = checkStringForPattern(clientName, DISALLOWED_CHARACTERS);
      const ConfirmMessage = () => {
        return (
          <span>
            <span style={{ display: 'block', marginBottom: 5 }}>
              This will sync all invoices for {clientName} with {curr?.name}.
            </span>{' '}
            {maybeHasInvalidClientName ? (
              <span>
                To ensure a smooth sync, we need to remove any characters that aren’t supported by{' '}
                {curr.name}. Click “Confirm” if you are happy for us to adjust your client's name,
                for example to "{removeCharactersFromString(clientName, DISALLOWED_CHARACTERS)}".
              </span>
            ) : null}
          </span>
        );
      };

      const provider = apps.find(app => app.provider === curr?.name);
      const icon = <img key={curr.id} alt={provider?.provider} src={provider?.logoUrl} />;

      const currentEntry: ListOption = {
        ...curr,
        name: curr.email,
        provider: curr.name,
        active: false,
        confirm: true,
        confirmMessage: <ConfirmMessage />,
        icon,
      };

      const header: HeadingOption = {
        type: 'header',
        name: curr.name,
      };

      if (!prev.length) {
        return [header, currentEntry];
      }

      const previousEntry = prev[idx - 1];

      if (previousEntry.name !== curr.name) {
        return [...prev, header, currentEntry];
      }
      return [...prev, currentEntry];
    },
    []
  );

  return {
    exportOptions,
    invoiceListOptions,
  };
}

export function filterInvoicesFactory(invoices: Array<RawInvoice>, query: InvoiceQuery) {
  const filterInvoices = (invoice: RawInvoice) => {
    const { status, projectIds, userIds } = query;
    const { project, user } = invoice;

    const doesNotMatchQueryStatus =
      (status !== 'all' && invoice.status !== status) ||
      // we don't want to include voided invoices when filtering by "All"
      (status === 'all' && invoice.status === InvoiceStatus.Void);

    if (doesNotMatchQueryStatus) {
      return false;
    }

    // For invoices, projectIds is always Array<number>
    const doesNotMatchQueryProjectIds =
      projectIds.length > 0 && project && !projectIds.includes(project.id);
    if (doesNotMatchQueryProjectIds) {
      return false;
    }

    // doesNotMatchQueryUserIds?
    return !(userIds.length > 0 && !userIds.includes(user.id));
  };

  return invoices.filter(filterInvoices);
}

export function invoicePreviewToReadableString(
  preview: InvoicePreviewCreationResponse | null,
  currencyFormatter: (value: number) => string
) {
  if (preview == null) {
    return null;
  }

  const invoiceCount = preview.invoices_count;
  const hoursCount = sum(preview.project_ids.map(details => details.hours_count));
  const totalCost = sum(preview.project_ids.map(details => details.total_cost.amount));

  const projectCount = preview.project_ids.length;
  const total = `${invoiceCount} ${invoiceCount > 1 ? 'invoices' : 'invoice'}`;
  const entries = `${hoursCount} ${hoursCount > 1 ? 'time entries' : 'time entry'}`;
  const projects = `${projectCount} ${projectCount > 1 ? 'projects' : 'project'}`;

  return `${total} totaling ${currencyFormatter(totalCost)}, covering ${projects} and ${entries}.`;
}

/**
 * Take the preview data returned by the backend make sure it has a common shape
 */
export function mergePreviewProjectIds(
  previewIds: Array<InvoicePreviewProjectDetails> = [],
  skippedIds: Array<InvoiceSkippedItemDetails> = [],
  variantMap: Record<number, ProjectVariant | undefined>
): Array<Partial<ProjectBase> & InvoicePreviewProjectDetails> {
  const projects: Array<Partial<ProjectBase> & InvoicePreviewProjectDetails> = previewIds.map(
    details => {
      const lookupProject = variantMap?.[details.project_id];
      const extractProject = lookupProject ? extractProjectBase(lookupProject) : {};

      return {
        ...extractProject,
        ...details,
      };
    }
  );
  const skippedProjects: Array<Partial<ProjectBase> & InvoicePreviewProjectDetails> =
    skippedIds.map(details => {
      const lookupProject = variantMap?.[details.id];
      const extractProject = lookupProject ? extractProjectBase(lookupProject) : {};

      const { error, id, ...rest } = details;
      return {
        ...extractProject,
        ...rest,
        project_id: id,
        total_cost: { fractional: 0, amount: 0, formatted: '0.00', currency_code: '' },
        hours_count: 0,
      };
    });

  return [...projects, ...skippedProjects];
}

export function formatInvoiceRange(from: string | null, to: string | null) {
  return `${from ? DateTime.fromISO(from).toLocaleString(DateTime.DATE_MED) : 'Anytime'} - ${
    to ? DateTime.fromISO(to).toLocaleString(DateTime.DATE_MED) : 'Anytime'
  }`;
}
